<script setup>
// Imports
import { css } from '@codemirror/lang-css'
import { html } from '@codemirror/lang-html'
import { javascript } from '@codemirror/lang-javascript'
import { EditorView } from '@codemirror/view'
import { Codemirror } from 'vue-codemirror'

// Props
const props = defineProps({
  defaultLanguage: { type: String, default: 'css' },
  allowedLanguages: { type: Array, default: () => ['css', 'js', 'html'] },
  readOnly: { type: Boolean, default: false },
  height: { type: [String, Number], default: '9rem' },
  tabSize: { type: Number, default: 2 },
  lineWrapping: { type: Boolean, default: true },
  showLanguageSelect: { type: Boolean, default: true },
  languageLabel: { type: String, default: 'Language' },
  error: { type: Boolean, default: true },

  editorClass: { type: [String, Array, Object], default: 'omni-code-editor' },
})

// Models
const modelValue = defineModel({ type: String, default: '' })

// Constants
const languageOptionsAll = [
  { label: 'CSS', value: 'css' },
  { label: 'JavaScript', value: 'js' },
  { label: 'HTML', value: 'html' },
]

const languageExtensions = {
  css: () => [css()],
  js: () => [javascript({ jsx: true, typescript: false })],
  html: () => [html()],
}

// Refs
const selectedLanguage = ref(normalizeLang(props.defaultLanguage))

// Computed properties
const allowedSet = computed(() => new Set(props.allowedLanguages.map(normalizeLang)))
const languageOptions = computed(() => languageOptionsAll.filter((opt) => allowedSet.value.has(opt.value)))

const extensions = computed(() => {
  const langExt = (languageExtensions[selectedLanguage.value] ?? languageExtensions.css)()
  return props.lineWrapping ? [...langExt, EditorView.lineWrapping] : langExt
})

const cmStyle = computed(() => ({
  width: '100%',
  minHeight: typeof props.height === 'number' ? `${props.height}px` : props.height,
  maxHeight: '50vh',
}))

// Methods
function normalizeLang(v) {
  return String(v ?? '')
    .toLowerCase()
    .trim()
}

// Watchers
watch(
  () => props.defaultLanguage,
  (v) => {
    const next = normalizeLang(v)
    if (allowedSet.value.has(next)) selectedLanguage.value = next
  },
)

watch(
  () => props.allowedLanguages,
  () => {
    if (!allowedSet.value.has(selectedLanguage.value)) {
      selectedLanguage.value = languageOptions.value[0]?.value ?? 'css'
    }
  },
  { deep: true },
)

// Lifecycle hooks

// Expose
</script>

<template>
  <div class="column q-gutter-sm" :class="editorClass">
    <div v-if="showLanguageSelect" class="row items-center">
      <q-select
        v-model="selectedLanguage"
        :options="languageOptions"
        :label="languageLabel"
        emit-value
        map-options
        dense
        outlined
        style="min-width: 180px"
        :disable="readOnly"
      />
    </div>

    <q-card flat bordered class="q-pa-none" :class="{ 'bg-grey-1': readOnly, 'omni-code-editor--error': error }" rounded>
      <q-card-section class="q-pa-none">
        <Codemirror
          v-model="modelValue"
          :extensions="extensions"
          :style="cmStyle"
          :disabled="readOnly"
          :tab-size="tabSize"
          :line-wrapping="lineWrapping"
          :indent-with-tab="true"
          style="width: 100%"
        />
      </q-card-section>
    </q-card>
  </div>
</template>
